import React, { useState, useEffect, useMemo } from "react";
import "../../css/CRUDModal.css";
import ButtonTriangle from "../../assets/Icons/Button Triangle.png";
import FilterIcon from "../../assets/Icons/Orange Filter.png";
import CrossIcon from "../../assets/Icons/cross_svg.svg";
import LeftCircle from "../../assets/Icons/Left circle B.png";
import RightCircle from "../../assets/Icons/Right circle A.png";
import Dropdown from "../Dropdown";
import { useForm, Controller } from "react-hook-form";
import { Box, CircularProgress } from "@mui/material";
import { axiosPostApi } from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";

const BagCourierFilterModal = ({
  show,
  handleClose,
  filterData,
  setFilterData,
  handleReset,
  bagList = [],
  applyFilters,
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      bagNo: filterData?.bagNo || "",
      fromDate: filterData?.fromDate || "",
      toDate: filterData?.toDate || "",
      createdBy: filterData?.createdBy || "",
    },
    mode: "onChange",
  });

  const watchedValues = watch();

  useEffect(() => {
    if (show) {
      reset({
        bagNo: filterData?.bagNo || "",
        fromDate: filterData?.fromDate || "",
        toDate: filterData?.toDate || "",
        createdBy: filterData?.createdBy || "",
      });
    }
  }, [show, filterData, reset]);

  const [selectedTab, setSelectedTab] = useState("Bag No");
  const [loading, setLoading] = useState(false);
  const [allBags, setAllBags] = useState([]);
  const loggedInUserName = sessionStorage.getItem("username");

  useEffect(() => {
    fetchAllBags();
  }, []);

  const fetchAllBags = async () => {
    try {
      const response = await axiosPostApi(
        `${apiList.FETCH_BAG_MASTER}?pageSize=1000&pageNo=1`,
        {
          username: loggedInUserName,
          statusList: ["NOTIFIED_HUB", "ACCEPTED_BY_HUB"],
        }
      );
      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00" &&
        Array.isArray(response?.data?.data)
      ) {
        setAllBags(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching all bags for filter options:", err);
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Bag No options (A-Z sorted, unique)
  const combinedBagNumbers = useMemo(() => {
    const bagSet = new Set();

    if (Array.isArray(allBags)) {
      allBags.forEach((item) => {
        const bag = item?.bagNo || item?.companyId;
        if (bag && typeof bag === "string" && bag.trim()) {
          bagSet.add(bag.trim());
        }
      });
    }

    if (Array.isArray(bagList)) {
      bagList.forEach((item) => {
        const bag = item?.bagNo || item?.companyId;
        if (bag && typeof bag === "string" && bag.trim()) {
          bagSet.add(bag.trim());
        }
      });
    }

    const list = Array.from(bagSet);
    list.sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base", numeric: true })
    );
    return list;
  }, [allBags, bagList]);

  const currentBagNo = watchedValues?.bagNo || filterData?.bagNo || "";

  const bagDropdownOptions = useMemo(() => {
    const list = combinedBagNumbers.map((bag) => ({
      label: bag,
      value: bag,
    }));

    if (currentBagNo) {
      return [
        { label: "✕ Clear Bag No Selection", value: "" },
        ...list,
      ];
    }
    return list;
  }, [combinedBagNumbers, currentBagNo]);

  // Created By options (A-Z sorted, unique)
  const combinedCreatedByList = useMemo(() => {
    const userSet = new Set();

    if (Array.isArray(allBags)) {
      allBags.forEach((item) => {
        const user = item?.createdBy;
        if (user && typeof user === "string" && user.trim()) {
          userSet.add(user.trim());
        }
      });
    }

    if (Array.isArray(bagList)) {
      bagList.forEach((item) => {
        const user = item?.createdBy;
        if (user && typeof user === "string" && user.trim()) {
          userSet.add(user.trim());
        }
      });
    }

    const list = Array.from(userSet);
    list.sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base", numeric: true })
    );
    return list;
  }, [allBags, bagList]);

  const currentCreatedBy =
    watchedValues?.createdBy || filterData?.createdBy || "";

  const createdByDropdownOptions = useMemo(() => {
    const list = combinedCreatedByList.map((user) => ({
      label: user,
      value: user,
    }));

    if (currentCreatedBy) {
      return [
        { label: "✕ Clear Created By Selection", value: "" },
        ...list,
      ];
    }
    return list;
  }, [combinedCreatedByList, currentCreatedBy]);

  const hasActiveFilter = (tabName) => {
    switch (tabName) {
      case "Bag No":
        return Boolean(filterData?.bagNo && filterData.bagNo.trim() !== "");
      case "Created Date":
        return Boolean(
          (filterData?.fromDate && filterData.fromDate.trim() !== "") ||
          (filterData?.toDate && filterData.toDate.trim() !== "")
        );
      case "Created By":
        return Boolean(
          filterData?.createdBy && filterData.createdBy.trim() !== ""
        );
      default:
        return false;
    }
  };

  const handleTabChange = (e, tabName) => {
    e.preventDefault();
    setSelectedTab(tabName);
  };

  const onSubmit = (formData) => {
    setFilterData(formData);
    if (applyFilters) {
      applyFilters(formData);
    }
    handleClose();
  };

  const onReset = () => {
    const emptyFilters = {
      bagNo: "",
      fromDate: "",
      toDate: "",
      createdBy: "",
    };
    reset(emptyFilters);
    setFilterData(emptyFilters);
    handleReset();
  };

  return (
    <>
      {show && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1060,
          }}
        >
          <div className="modal-dialog" role="document">
            <div
              className="modal-content"
              style={{
                width: "40vw",
                borderRadius: "24px",
                padding: "24px",
                backgroundColor: "#FFF",
                border: "1px solid rgba(0, 0, 0, 0.175)",
              }}
            >
              <img
                src={LeftCircle}
                alt="leftImg"
                className="absolute left-0 bottom-0 w-[110px] h-[105px]"
              />
              <img
                src={RightCircle}
                alt="leftImg"
                className="absolute right-0 top-0 w-[110px] h-[105px]"
              />
              <div
                className="modal-header"
                style={{
                  padding: "1rem 1rem",
                  borderBottom: "1px solid #dee2e6",
                  justifyContent: "space-between",
                  display: "flex",
                }}
              >
                <div className="flex items-center gap-2">
                  <img src={FilterIcon} alt="filter icon" className="w-6 h-6" />
                  <h5 className="modal-title main-title">Filter</h5>
                </div>
                <img
                  src={CrossIcon}
                  alt="close icon"
                  className="w-[30px] h-[30px] cursor-pointer z-20"
                  onClick={handleClose}
                />
              </div>
              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <div
                  className="scoped-modal modal-body"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <div className="tab-container">
                    <ul className="tab-list">
                      {["Bag No", "Created Date", "Created By"].map((tab) => (
                        <div className="tab-wrapper" key={tab}>
                          <li
                            className={
                              selectedTab === tab
                                ? "tab-item selected"
                                : "tab-item"
                            }
                            style={
                              hasActiveFilter(tab) && selectedTab !== tab
                                ? {
                                    outline: "2px solid #FF6B35",
                                    outlineOffset: "2px",
                                  }
                                : {}
                            }
                          >
                            <button
                              className={`tab-button ${
                                selectedTab === tab ? "active" : ""
                              }`}
                              onClick={(e) => handleTabChange(e, tab)}
                            >
                              {tab}
                            </button>
                          </li>
                          {selectedTab === tab && (
                            <img
                              src={ButtonTriangle}
                              alt="tri"
                              className="tab-triangle"
                            />
                          )}
                        </div>
                      ))}
                    </ul>

                    <div className="tab-content !bg-[linear-gradient(to_bottom,#FFE8D4_0%_100%,transparent_25%_100%)]">
                      {/* Bag No Tab */}
                      {selectedTab === "Bag No" && (
                        <Controller
                          name="bagNo"
                          control={control}
                          render={({ field }) => (
                            <div className="dateField">
                              <label className="dateLabel">Bag No</label>
                              <div className="dateInputWrapper">
                                <Dropdown
                                  placeholder="Select Bag No"
                                  options={bagDropdownOptions}
                                  value={
                                    field.value ||
                                    watchedValues?.bagNo ||
                                    filterData?.bagNo ||
                                    ""
                                  }
                                  labelKey="label"
                                  valueKey="value"
                                  showSearch={true}
                                  selectClassName="!h-[38px] !rounded-[6px]"
                                  onChange={(item) => {
                                    const selectedBag =
                                      item && item.value ? item.value : "";
                                    field.onChange(selectedBag);
                                    setValue("bagNo", selectedBag);
                                    setFilterData((prev) => ({
                                      ...prev,
                                      bagNo: selectedBag,
                                    }));
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        />
                      )}

                      {/* Created Date Tab */}
                      {selectedTab === "Created Date" && (
                        <div
                          className="dateGrid"
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "1rem",
                            alignItems: "flex-start",
                          }}
                        >
                          <Controller
                            name="fromDate"
                            control={control}
                            rules={{
                              validate: {
                                validDate: (value) =>
                                  !value ||
                                  !isNaN(Date.parse(value)) ||
                                  "Invalid From Date",
                                beforeToDate: (value, formValues) =>
                                  !value ||
                                  !formValues.toDate ||
                                  new Date(value) <=
                                    new Date(formValues.toDate) ||
                                  "From Date must be before To Date",
                              },
                            }}
                            render={({ field }) => (
                              <div className="dateField" style={{ flex: 1 }}>
                                <label className="dateLabel">From Date</label>
                                <div className="dateInputWrapper">
                                  <input
                                    {...field}
                                    type="date"
                                    className="dateInput"
                                    max={getTodayDate()}
                                    value={field.value || ""}
                                    onChange={(e) => {
                                      field.onChange(e.target.value);
                                      setValue("fromDate", e.target.value);
                                      setFilterData((prev) => ({
                                        ...prev,
                                        fromDate: e.target.value,
                                      }));
                                    }}
                                  />
                                  {errors?.fromDate && (
                                    <div className="error-text">
                                      {errors.fromDate.message}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          />
                          <Controller
                            name="toDate"
                            control={control}
                            rules={{
                              validate: {
                                validDate: (value) =>
                                  !value ||
                                  !isNaN(Date.parse(value)) ||
                                  "Invalid To Date",
                                afterFromDate: (value, formValues) =>
                                  !value ||
                                  !formValues.fromDate ||
                                  new Date(value) >=
                                    new Date(formValues.fromDate) ||
                                  "To Date must be after From Date",
                              },
                            }}
                            render={({ field }) => (
                              <div className="dateField" style={{ flex: 1 }}>
                                <label className="dateLabel">To Date</label>
                                <div className="dateInputWrapper">
                                  <input
                                    {...field}
                                    type="date"
                                    className="dateInput"
                                    max={getTodayDate()}
                                    value={field.value || ""}
                                    onChange={(e) => {
                                      field.onChange(e.target.value);
                                      setValue("toDate", e.target.value);
                                      setFilterData((prev) => ({
                                        ...prev,
                                        toDate: e.target.value,
                                      }));
                                    }}
                                  />
                                  {errors?.toDate && (
                                    <div className="error-text">
                                      {errors.toDate.message}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          />
                        </div>
                      )}

                      {/* Created By Tab */}
                      {selectedTab === "Created By" && (
                        <Controller
                          name="createdBy"
                          control={control}
                          render={({ field }) => (
                            <div className="dateField">
                              <label className="dateLabel">Created By</label>
                              <div className="dateInputWrapper">
                                <Dropdown
                                  placeholder="Select Created By"
                                  options={createdByDropdownOptions}
                                  value={
                                    field.value ||
                                    watchedValues?.createdBy ||
                                    filterData?.createdBy ||
                                    ""
                                  }
                                  labelKey="label"
                                  valueKey="value"
                                  showSearch={true}
                                  selectClassName="!h-[38px] !rounded-[6px]"
                                  onChange={(item) => {
                                    const selectedUser =
                                      item && item.value ? item.value : "";
                                    field.onChange(selectedUser);
                                    setValue("createdBy", selectedUser);
                                    setFilterData((prev) => ({
                                      ...prev,
                                      createdBy: selectedUser,
                                    }));
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className="scoped-modal"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "12px",
                    paddingBottom: "0px",
                    gap: "16px",
                    borderTop: "1px solid #dee2e6",
                  }}
                >
                  <div className="buttonContainer">
                    <button
                      type="button"
                      className="btn btn-back w-[133px] !rounded-lg"
                      onClick={onReset}
                    >
                      Reset
                    </button>
                  </div>
                  <div className="buttonContainer">
                    <button
                      type="submit"
                      className="btn btn-submit w-[133px] !rounded-lg"
                      disabled={isSubmitting}
                    >
                      {loading ? (
                        <CircularProgress
                          style={{ width: "20px", height: "20px" }}
                        />
                      ) : (
                        "Apply"
                      )}
                    </button>
                  </div>
                </div>
              </Box>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BagCourierFilterModal;
